import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CampaignOutlined,
  EmailOutlined,
  GroupOutlined,
  PersonAddAlt1Outlined,
  PhoneCallbackOutlined,
} from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material'

import { CaseIcon, ProcessIcon } from '~assets/icons'

import AddContactDialog from './ContactsScreen/components/AddContactDialog'
import StartProcessDialog from './ProcessesScreen/StartProcessDialog'

interface QuickCreateDialogProps {
  open: boolean
  onClose: () => void
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '400px',
    width: '100%',
    margin: theme.spacing(2),
    borderRadius: theme.spacing(2),
  },
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  padding: theme.spacing(3),
}))

const StyledList = styled(List)(({ theme }) => ({
  padding: theme.spacing(0, 2, 2, 2),
}))

const StyledListItem = styled(ListItem)({
  padding: '4px 0',
})

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: '9999px',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
    color: theme.palette.grey[500],
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '&:hover .MuiListItemText-primary': {
    color: theme.palette.text.primary,
  },
}))

const QuickCreateDialog: React.FC<QuickCreateDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate()
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false)
  const [isProcessSelectionOpen, setIsProcessSelectionOpen] = useState(false)

  const handleAddContactClick = (): void => {
    onClose()
    setIsAddContactDialogOpen(true)
  }

  const handleStartProcessClick = (): void => {
    onClose()
    setIsProcessSelectionOpen(true)
  }

  const handleNavigation = (path: string): void => {
    onClose()
    navigate(path)
  }

  const menuItems = [
    {
      icon: <CaseIcon color={'grey'} />,
      label: 'Open a Case',
      onClick: (): void => handleNavigation('/create-case/ViaSite'),
    },
    {
      icon: <ProcessIcon />,
      label: 'Start a Process',
      onClick: (): void => handleStartProcessClick(),
    },
    {
      icon: <EmailOutlined />,
      label: 'Log an Email',
      onClick: (): void => handleNavigation('/create-case/ViaEmail'),
    },
    {
      icon: <PhoneCallbackOutlined sx={{ color: 'success.main' }} />,
      label: 'Log a Phone Call',
      onClick: (): void => handleNavigation('/create-case/ViaPhone'),
    },
    {
      icon: <PersonAddAlt1Outlined sx={{ color: 'error.main' }} />,
      label: 'Log an In-Person Visit',
      onClick: (): void => handleNavigation('/create-case/ViaInPerson'),
    },
    {
      icon: <GroupOutlined />,
      label: 'Add a Contact',
      onClick: handleAddContactClick,
    },
    {
      icon: <CampaignOutlined />,
      label: 'Send a Mass Communication',
      onClick: (): void => handleNavigation('/mass-communication'),
    },
  ]

  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <StyledDialogTitle>Quick Create</StyledDialogTitle>
        <StyledList>
          {menuItems.map((item, index) => (
            <StyledListItem key={index} disablePadding>
              <StyledListItemButton onClick={item.onClick}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </StyledListItemButton>
            </StyledListItem>
          ))}
        </StyledList>
      </StyledDialog>

      <AddContactDialog
        open={isAddContactDialogOpen}
        onClose={() => setIsAddContactDialogOpen(false)}
      />

      <StartProcessDialog
        open={isProcessSelectionOpen}
        onClose={() => setIsProcessSelectionOpen(false)}
      />
    </>
  )
}

export default QuickCreateDialog
